import { useState, useEffect } from "react";
import API from "../../../api";
import AdminCustomerProfile from "./AdminCustomerProfile";
import AdminCustomerPassword from "./AdminCustomerPassword";
import AdminOrdersList from "../../orders/admin/AdminOrdersList";
import { swalAlert } from "../../../utils";
import "./admin-customer-edit.css";
import "./../../../css/forms.css";

function AdminCustomerEdit({ customer, onCreated, onUpdated }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [editedCustomer, setEditedCustomer] = useState({});
  const [saving, setSaving] = useState(false);

  // -----------------------------
  // INIT
  // -----------------------------

  useEffect(() => {
    setActiveTab("profile");

    if (!customer?.userId) {
      setEditedCustomer({
        userType: "cust",
        userStatus: "active",
        userName: "",
        userFirstname: "",
        userEmail: "",
        password: "",
        sendCredentials: false,
      });
    } else {
      setEditedCustomer({
        ...customer,
        password: "",
        sendCredentials: false,
      });
    }
  }, [customer]);

  // -----------------------------
  // CHANGE
  // -----------------------------

  const handleChange = (payload) => {
    setEditedCustomer((prev) => ({
      ...prev,
      ...payload,
    }));
  };

  // -----------------------------
  // SAVE
  // -----------------------------

  const handleSave = async () => {
    setSaving(true);

    try {
      let response;

      if (editedCustomer.userId) {
        response = await API.put(
          `/admin/customer/${editedCustomer.userId}`,
          editedCustomer
        );
        onUpdated?.(response.data);
      } else {
        response = await API.post(
          "/admin/customer",
          editedCustomer
        );
        onCreated?.(response.data);
      }

      setEditedCustomer({
        ...response.data,
        password: "",
        sendCredentials: false,
      });
    } catch (error) {
      let message =
        error?.response?.data?.error?.message ||
        "Erreur lors de la sauvegarde";

      if (/duplicate/i.test(message)) {
        message =
          "Un compte client existe déjà avec cette adresse email.";
      }

      swalAlert(message, error);
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------
  // VALIDATION
  // -----------------------------

  const isPasswordInvalid =
    editedCustomer.password?.length > 0 &&
    editedCustomer.passwordIsValid === false;

  const isNewWithoutPassword =
    !editedCustomer.userId &&
    (!editedCustomer.password || editedCustomer.password.length === 0);

  // -----------------------------
  // RENDER
  // -----------------------------

  return (
    <div id="CustomerEdit">
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <a
            className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            Profil
          </a>
        </li>

        <li className="nav-item d-none">
          <a
            className={`nav-link ${activeTab === "orders" ? "active" : ""}`}
            onClick={() => setActiveTab("orders")}
          >
            Commandes
          </a>
        </li>

        <li className="nav-item d-none">
          <a
            className={`nav-link ${activeTab === "password" ? "active" : ""}`}
            onClick={() => setActiveTab("password")}
          >
            Mot de passe
          </a>
        </li>

        <li className="nav-item ms-auto">
          <button
            className="btn btn-green"
            onClick={handleSave}
            disabled={isPasswordInvalid || isNewWithoutPassword}
          >
            {saving && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            Sauvegarder
          </button>
        </li>
      </ul>

      <div className="tab-contents">
        {activeTab === "profile" && (
          <AdminCustomerProfile
            customer={editedCustomer}
            onChange={handleChange}
          />
        )}

        {activeTab === "orders" && editedCustomer.userId && (
          <AdminOrdersList
            query={{ "userId:eq": editedCustomer.userId }}
            title={false}
            selector={false}
            thead={false}
            columns={[
              "orderDate",
              "orderRef",
              "articles",
              "delivery",
              "download",
            ]}
          />
        )}

        {activeTab === "password" && (
          <>
            <AdminCustomerPassword
              defaultValue={editedCustomer.password}
              onChange={(password) =>
                handleChange(password)
              }
            />

            <div className="form-group mb-3">
              <label className="checkbox-label fw-bold">
                <input
                  type="checkbox"
                  checked={
                    editedCustomer.password !== "" &&
                    editedCustomer.sendCredentials
                  }
                  onChange={(e) =>
                    handleChange({
                      sendCredentials: e.target.checked,
                    })
                  }
                  disabled={editedCustomer.password === ""}
                />
                Envoyer les identifiants par mail
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminCustomerEdit;